import React from "react";
import QRCode from "react-qr-code";
import { AppStore, PlayStore } from "../../features/theme/icons";
import "./LinkRenderer.css";

// Utility function to detect URLs in text
export const detectUrls = (text) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const urls = text.match(urlRegex);
  return urls || [];
};

// Utility function to check if URL is an app store link
export const isAppStoreLink = (url) => {
  const appStorePatterns = [/apps\.apple\.com/, /itunes\.apple\.com/];
  return appStorePatterns.some((pattern) => pattern.test(url));
};

export const isPlayStoreLink = (url) => {
  const playStorePatterns = [
    /play\.google\.com\/store\/apps/,
    /play\.google\.com\/store\/movies/,
    /play\.google\.com\/store\/books/,
  ];
  return playStorePatterns.some((pattern) => pattern.test(url));
};

// Component to render a single link
const LinkItem = ({ url, isAppStore, isPlayStore }) => {
  if (isAppStore || isPlayStore) {
    return (
      <div className="app-store-link-container">
        <div className="qr-code-container">
          <a href={url} target="_blank" rel="noopener noreferrer">
            <div className="qr-code-wrapper">
              <QRCode value={url} size={128} level="M" />
              <div className="store-icon-overlay">
                <img
                  src={isAppStore ? AppStore : PlayStore}
                  alt={isAppStore ? "App Store" : "Play Store"}
                  className="store-icon"
                />
              </div>
            </div>
            <p className="qr-instruction">Scan to open</p>
          </a>
        </div>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="app-store-link"
        >
          {url}
        </a>
      </div>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="regular-link"
    >
      {url}
    </a>
  );
};

// Function to parse markdown formatting (bold, italic) in text
const parseMarkdownFormatting = (text) => {
  const parts = [];
  let lastIndex = 0;
  let key = 0;

  // Pattern to match: **bold**, __bold__, *italic*, _italic_
  // But avoid matching URLs
  const patterns = [
    { regex: /\*\*([^*]+)\*\*/g, type: "bold" }, // **bold**
    { regex: /__([^_]+)__/g, type: "bold" }, // __bold__
    { regex: /\*([^*\n]+)\*/g, type: "italic" }, // *italic* (but not **)
    { regex: /_([^_\n]+)_/g, type: "italic" }, // _italic_ (but not __)
  ];

  // Find all matches with their positions
  const matches = [];
  patterns.forEach(({ regex, type }) => {
    let match;
    regex.lastIndex = 0; // Reset regex
    while ((match = regex.exec(text)) !== null) {
      // Check if this match is part of a URL
      const beforeMatch = text.substring(
        Math.max(0, match.index - 10),
        match.index
      );
      const afterMatch = text.substring(
        match.index,
        Math.min(text.length, match.index + match[0].length + 10)
      );
      const isInUrl = /https?:\/\//.test(beforeMatch + afterMatch);

      if (!isInUrl) {
        matches.push({
          start: match.index,
          end: match.index + match[0].length,
          content: match[1],
          type: type,
          fullMatch: match[0],
        });
      }
    }
  });

  // Sort matches by position
  matches.sort((a, b) => a.start - b.start);

  // Remove overlapping matches (prefer bold over italic if they overlap)
  const filteredMatches = [];
  matches.forEach((match) => {
    const overlaps = filteredMatches.some(
      (existing) => match.start < existing.end && match.end > existing.start
    );
    if (!overlaps) {
      filteredMatches.push(match);
    }
  });

  // Build parts array
  filteredMatches.forEach((match) => {
    // Add text before the match
    if (match.start > lastIndex) {
      const beforeText = text.substring(lastIndex, match.start);
      if (beforeText) {
        parts.push({ type: "text", content: beforeText, key: key++ });
      }
    }

    // Add the formatted match
    if (match.type === "bold") {
      parts.push({ type: "bold", content: match.content, key: key++ });
    } else if (match.type === "italic") {
      parts.push({ type: "italic", content: match.content, key: key++ });
    }

    lastIndex = match.end;
  });

  // Add remaining text
  if (lastIndex < text.length) {
    const remainingText = text.substring(lastIndex);
    if (remainingText) {
      parts.push({ type: "text", content: remainingText, key: key++ });
    }
  }

  // If no matches found, return original text
  if (parts.length === 0) {
    return [{ type: "text", content: text, key: 0 }];
  }

  return parts;
};

// Function to render parsed markdown parts (without URL processing to avoid recursion)
const renderMarkdownParts = (parts) => {
  return parts.map((part) => {
    if (part.type === "bold") {
      return (
        <strong key={part.key} style={{ fontWeight: "bold" }}>
          {part.content}
        </strong>
      );
    } else if (part.type === "italic") {
      return (
        <em key={part.key} style={{ fontStyle: "italic" }}>
          {part.content}
        </em>
      );
    } else {
      return <span key={part.key}>{part.content}</span>;
    }
  });
};

// Function to process text: first extract URLs, then parse markdown in text parts
const processTextWithMarkdownAndUrls = (text) => {
  const urls = detectUrls(text);

  if (urls.length === 0) {
    // No URLs, just parse markdown
    const markdownParts = parseMarkdownFormatting(text);
    return renderMarkdownParts(markdownParts);
  }

  // Split by URLs and process each part
  let lastIndex = 0;
  const parts = [];

  urls.forEach((url, index) => {
    const urlIndex = text.indexOf(url, lastIndex);

    // Add text before the URL (with markdown parsing)
    if (urlIndex > lastIndex) {
      const beforeText = text.substring(lastIndex, urlIndex);
      if (beforeText) {
        const markdownParts = parseMarkdownFormatting(beforeText);
        parts.push(...renderMarkdownParts(markdownParts));
      }
    }

    // Add the URL
    parts.push(
      <LinkItem
        key={`link-${index}`}
        url={url}
        isAppStore={isAppStoreLink(url)}
        isPlayStore={isPlayStoreLink(url)}
      />
    );

    lastIndex = urlIndex + url.length;
  });

  // Add remaining text after the last URL
  if (lastIndex < text.length) {
    const remainingText = text.substring(lastIndex);
    if (remainingText) {
      const markdownParts = parseMarkdownFormatting(remainingText);
      parts.push(...renderMarkdownParts(markdownParts));
    }
  }

  return parts;
};

// Function to format text with markdown-style bullet points, bold, and italics
const formatTextWithMarkdown = (text) => {
  // Split by lines to handle line breaks and bullet points
  const lines = text.split("\n");
  const formattedParts = [];
  let listItems = [];
  let listKey = 0;

  lines.forEach((line, lineIndex) => {
    const trimmedLine = line.trim();

    // Check if line is a bullet point (starts with -, *, or •)
    if (/^[-*•]\s/.test(trimmedLine)) {
      // Extract the bullet point text
      const bulletText = trimmedLine.replace(/^[-*•]\s/, "");
      listItems.push(bulletText);
    } else {
      // If we have accumulated list items, render them as a list
      if (listItems.length > 0) {
        formattedParts.push(
          <ul
            key={`list-${listKey++}`}
            style={{ margin: "8px 0", paddingLeft: "20px" }}
          >
            {listItems.map((item, idx) => (
              <li key={idx} style={{ marginBottom: "4px" }}>
                {processTextWithMarkdownAndUrls(item)}
              </li>
            ))}
          </ul>
        );
        listItems = [];
      }

      // Add the regular line (or empty line for spacing)
      if (trimmedLine || lineIndex === 0) {
        formattedParts.push(
          <div
            key={`line-${lineIndex}`}
            style={{ marginBottom: trimmedLine ? "8px" : "4px" }}
          >
            {trimmedLine ? processTextWithMarkdownAndUrls(trimmedLine) : <br />}
          </div>
        );
      }
    }
  });

  // Handle any remaining list items
  if (listItems.length > 0) {
    formattedParts.push(
      <ul
        key={`list-${listKey++}`}
        style={{ margin: "8px 0", paddingLeft: "20px" }}
      >
        {listItems.map((item, idx) => (
          <li key={idx} style={{ marginBottom: "4px" }}>
            {renderMarkdownParts(parseMarkdownFormatting(item))}
          </li>
        ))}
      </ul>
    );
  }

  return formattedParts.length > 0
    ? formattedParts
    : processTextWithMarkdownAndUrls(text);
};

// Main component to render text with links and markdown formatting
const LinkRenderer = ({ content }) => {
  // Check if content contains markdown-style formatting (bullet points, multiple lines, bold, or italic)
  const hasMarkdown =
    content.includes("\n") ||
    /^[-*•]\s/m.test(content) ||
    /\*\*[^*]+\*\*/.test(content) ||
    /\*[^*\n]+\*/.test(content) ||
    /__[^_]+__/.test(content) ||
    /_[^_\n]+_/.test(content);

  if (hasMarkdown) {
    return (
      <div style={{ whiteSpace: "pre-wrap", lineHeight: "1.5" }}>
        {formatTextWithMarkdown(content)}
      </div>
    );
  }

  // For simple text without markdown, use the processor (handles URLs and any markdown)
  return <span>{processTextWithMarkdownAndUrls(content)}</span>;
};

export default LinkRenderer;
